import { firebaseAuth } from '../firebase/auth';
import { Goal, Milestone, DailyTask, GoalStats, GoalTrends, GoalDuration } from '../../types/goals';
import { parsedParamsUrlForPagination } from '../utils';
import i18n from '../../languages/i18n';

const API_URL = import.meta.env.VITE_API_URL;

interface GoalFilters {
  page?: number;
  limit?: number;
  completed?: boolean;
  duration?: GoalDuration;
  startDate?: Date;
  endDate?: Date;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  searchTerm?: string;
}

interface GoalsResponse {
  goals: Goal[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  stats?: GoalStats;
}

export interface GoalDetailsResponse {
  goal: any;
  milestones: any[];
  dailyTasks: any[];
  stats: {
    totalMilestones: number;
    completedMilestones: number;
    totalTasks: number;
    completedTasks: number;
    progressPercentage: number;
    daysRemaining: number;
    isOnTrack: boolean;
  };
}

// Types for progress statistics
export interface GoalStat {
  title: string;
  value: number;
  label: string;
  trend: {
    percentage: number;
    direction: 'up' | 'down' | 'neutral';
  };
  color: string;
  icon: string;
  progress?: number;
  timeRange: 'day' | 'week' | 'month' | 'quarter' | 'year';
  pulseEffect?: boolean;
  comparison?: string;
}

export interface GoalProgress {
  id: string;
  title: string;
  value: number;
  label: string;
  trend: {
    percentage: number;
    direction: 'up' | 'down' | 'neutral';
  };
  color: string;
  progress: number;
  comparison?: string;
  timeRange: string;
  additionalStats?: Array<{
    label: string;
    value: string | number;
  }>;
  showDetailedTrend?: boolean;
  pulseEffect?: boolean;
}

class GoalsAPI {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const idToken = await firebaseAuth.getIdToken();
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
      ...options.headers
    };

    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || 'Request failed');
      }

      return response.json();
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  }

  async getGoals(filters: GoalFilters = {}): Promise<{
    data: Goal[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> {
    const queryParams = this.buildQueryParams(filters);
    const response = await this.request<GoalsResponse>(`/api/goals?${queryParams}`);

    return {
      data: response.goals.map(this.parseGoalDates),
      pagination: response.pagination,
    };
  }

  async getGoalsStats(): Promise<GoalStats> {
    return this.request<GoalStats>('/api/goals/stats');
  }

  async getGoalDetails(goalId: string): Promise<GoalDetailsResponse> {
    const data = await this.request(`/api/goals/${goalId}/details`) as GoalDetailsResponse;
    return {
      goal: this.parseGoalDates(data.goal),
      milestones: data.milestones.map(this.parseMilestoneDates),
      dailyTasks: data.dailyTasks.map(this.parseTaskDates),
      stats: data.stats
    };
  }

  async createGoal(data: {
    title: string;
    description: string;
    startDate: Date;
    endDate: Date;
    duration: GoalDuration;
  }): Promise<any> {
    const response = await this.request<any>('/api/goals', {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        startDate: data.startDate.toISOString(),
        endDate: data.endDate.toISOString()
      })
    });

    return {
      goal: this.parseGoalDates(response.goal),
      milestones: response.milestones.map(this.parseMilestoneDates),
      dailyTasks: response.dailyTasks.map(this.parseTaskDates)
    };
  }

  async updateGoalProgress(goalId: string, progress: number): Promise<Goal> {
    const response = await this.request<Goal>(`/api/goals/${goalId}/progress`, {
      method: 'PATCH',
      body: JSON.stringify({ progress })
    });

    return this.parseGoalDates(response);
  }

  async updateGoalStatus(goalId: string, isCompleted: boolean): Promise<Goal> {
    const response = await this.request<Goal>(`/api/goals/${goalId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ isCompleted })
    });

    return this.parseGoalDates(response);
  }

  async getGoalStats(): Promise<GoalStats> {
    return this.request<GoalStats>('/api/goals/stats');
  }

  async getGoalTrends(timeframe: 'week' | 'month' | 'year' = 'month'): Promise<GoalTrends> {
    return this.request<GoalTrends>(`/api/goals/trends?timeframe=${timeframe}`);
  }

  /**
   * Obtiene estadísticas de resumen para la visualización en componentes ProgressStats
   * @param timeRange Rango de tiempo para las estadísticas
   * @returns Estadísticas formateadas para los componentes
   */
  async getSummaryStats(timeRange: 'day' | 'week' | 'month' | 'quarter' | 'year' = 'month'): Promise<GoalStat[]> {
    try {
      const response = await this.request<GoalStat[]>(`/api/goals/summary-stats?timeRange=${timeRange}`);

      // Mapeo de títulos en español a claves de traducción
      const titleTranslationKeys = {
        "Total de Objetivos": "stats.totalGoals",
        "Objetivos Completados": "stats.completedGoals",
        "Objetivos al Día": "stats.onTrackGoals",
        "Objetivos Atrasados": "stats.behindGoals"
      };

      // Mapeo de etiquetas en español a claves de traducción
      const labelTranslationKeys = {
        "objetivos": "stats.goalsLabel",
        "completados": "stats.completedLabel",
        "en progreso": "stats.inProgressLabel",
        "atrasados": "stats.behindLabel"
      };

      // Obtener idioma actual
      const currentLang = i18n.language || 'en';
      const shouldTranslate = !currentLang.startsWith('es');

      // Aplicar traducciones a los resultados
      const translatedStats = response.map(stat => {
        if (!shouldTranslate) {
          return stat;
        }

        const titleKey = titleTranslationKeys[stat.title];
        const labelKey = labelTranslationKeys[stat.label];

        return {
          ...stat,
          title: titleKey ? i18n.t(titleKey) : stat.title,
          label: labelKey ? i18n.t(labelKey) : stat.label
        };
      });

      return translatedStats;
    } catch (error) {
      console.error('Error fetching summary stats:', error);

      // Datos de respaldo en caso de error
      return [
        {
          title: i18n.t("stats.totalGoals"),
          value: 5,
          label: i18n.t("stats.goalsLabel"),
          trend: { percentage: 20, direction: 'up' },
          color: "blue",
          icon: "GoalIcon",
          timeRange: timeRange,
        },
        {
          title: i18n.t("stats.completedGoals"),
          value: 2,
          label: i18n.t("stats.completedLabel"),
          trend: { percentage: 40, direction: 'up' },
          color: "green",
          icon: "CheckCircle",
          progress: 40,
          timeRange: timeRange,
        },
        {
          title: i18n.t("stats.onTrackGoals"),
          value: 2,
          label: i18n.t("stats.inProgressLabel"),
          trend: { percentage: 25, direction: 'up' },
          color: "teal",
          icon: "TrendingUp",
          progress: 50,
          timeRange: timeRange,
        },
        {
          title: i18n.t("stats.behindGoals"),
          value: 1,
          label: i18n.t("stats.behindLabel"),
          trend: { percentage: 10, direction: 'down' },
          color: "orange",
          icon: "AlertTriangle",
          progress: 15,
          timeRange: timeRange,
          pulseEffect: true
        }
      ];
    }
  }

  /**
   * Obtiene estadísticas de progreso detalladas para objetivos específicos
   * @param timeRange Rango de tiempo para las estadísticas
   * @param goalId ID del objetivo específico (opcional)
   * @returns Datos de progreso formateados
   */
  async getProgressStats(
    timeRange: 'day' | 'week' | 'month' | 'quarter' | 'year' = 'month',
    goalId?: string
  ): Promise<GoalProgress[]> {
    try {
      const url = goalId
        ? `/api/goals/progress-stats?timeRange=${timeRange}&goalId=${goalId}`
        : `/api/goals/progress-stats?timeRange=${timeRange}`;

      const response = await this.request<GoalProgress | GoalProgress[]>(url);
      const progressArray = Array.isArray(response) ? response : [response];

      // Mapeo de títulos para traducción
      const titleMap = {
        "Objetivo Ejemplo": "examples.exampleGoal",
        "Aprender React Native": "examples.learnReactNative",
        "Completar Curso de TypeScript": "examples.completeTypeScriptCourse",
        "Proyecto Personal": "examples.personalProject"
      };

      // Mapeo de etiquetas para traducción
      const labelMap = {
        "Días restantes": "stats.daysRemaining",
        "Tareas completadas": "stats.completedTasks",
        "Hitos completados": "stats.completedMilestones",
        "Tareas vencidas": "stats.overdueTasks"
      };

      // Obtener idioma actual
      const currentLang = i18n.language || 'en';
      const shouldTranslate = !currentLang.startsWith('es');

      // Traducir los resultados
      return progressArray.map(progress => {
        if (!shouldTranslate) {
          return progress;
        }

        // Traducir título
        let title = progress.title;
        if (titleMap[title]) {
          title = i18n.t(titleMap[title]);
        }

        // Traducir comparación
        let comparison = progress.comparison || "";
        if (comparison.includes("por encima de lo esperado")) {
          const percentage = comparison.split("%")[0].trim();
          comparison = `${percentage}% ${i18n.t("stats.aboveExpected")}`;
        } else if (comparison.includes("por debajo de lo esperado")) {
          const percentage = comparison.split("%")[0].trim();
          comparison = `${percentage}% ${i18n.t("stats.belowExpected")}`;
        }

        // Traducir estadísticas adicionales
        const additionalStats = progress.additionalStats?.map(stat => ({
          label: labelMap[stat.label] ? i18n.t(labelMap[stat.label]) : stat.label,
          value: stat.value
        }));

        return {
          ...progress,
          title,
          comparison,
          additionalStats
        };
      });
    } catch (error) {
      console.error('Error fetching goal progress:', error);

      // Datos de respaldo simplificados
      const fallbackData = [{
        id: goalId || "1",
        title: i18n.t("examples.exampleGoal"),
        value: 45,
        label: "%",
        trend: { percentage: 12, direction: 'up' },
        color: "green",
        progress: 45,
        comparison: `12% ${i18n.t("stats.aboveExpected")}`,
        timeRange: timeRange,
        additionalStats: [
          { label: i18n.t("stats.daysRemaining"), value: 14 },
          { label: i18n.t("stats.completedTasks"), value: "8/15" }
        ],
        showDetailedTrend: true
      }];

      return fallbackData;
    }
  }

  /**
   * Obtiene estadísticas y datos calculados para el dashboard de objetivos
   * @returns Datos para el panel de control
   */
  async getDashboardData(timeRange: 'day' | 'week' | 'month' = 'month'): Promise<{
    summaryStats: GoalStat[];
    goalProgress: GoalProgress[];
    calculatedStats: {
      totalGoals: number;
      completedGoals: number;
      pendingGoals: number;
      completionRate: number;
      onTrack: number;
      behind: number;
    };
  }> {
    try {
      // Obtener datos en paralelo
      const [summaryStats, goalProgress] = await Promise.all([
        this.getSummaryStats(timeRange),
        this.getProgressStats(timeRange)
      ]);

      // Títulos traducidos
      const totalGoalsTitle = i18n.t("stats.totalGoals");
      const completedGoalsTitle = i18n.t("stats.completedGoals");
      const onTrackGoalsTitle = i18n.t("stats.onTrackGoals");
      const behindGoalsTitle = i18n.t("stats.behindGoals");

      // Calcular estadísticas
      const totalGoals = summaryStats.find(stat => stat.title === totalGoalsTitle)?.value || 0;
      const completedGoals = summaryStats.find(stat => stat.title === completedGoalsTitle)?.value || 0;
      const onTrack = summaryStats.find(stat => stat.title === onTrackGoalsTitle)?.value || 0;
      const behind = summaryStats.find(stat => stat.title === behindGoalsTitle)?.value || 0;

      return {
        summaryStats,
        goalProgress,
        calculatedStats: {
          totalGoals,
          completedGoals,
          pendingGoals: totalGoals - completedGoals,
          completionRate: totalGoals > 0 ? Math.round((completedGoals / totalGoals) * 100) : 0,
          onTrack,
          behind
        }
      };
    } catch (error) {
      throw error;
    }
  }

  private buildQueryParams(filters: GoalFilters): string {
    const params = parsedParamsUrlForPagination(filters);
    return params;
  }

  private parseGoalDates(goal: any): Goal {
    if (!goal) return goal;

    return {
      ...goal,
      startDate: new Date(goal.startDate),
      endDate: new Date(goal.endDate),
      createdAt: new Date(goal.createdAt),
      lastUpdated: goal.lastUpdated ? new Date(goal.lastUpdated) : undefined
    };
  }

  private parseMilestoneDates(milestone: any): Milestone {
    if (!milestone) return milestone;

    return {
      ...milestone,
      dueDate: milestone.dueDate ? new Date(milestone.dueDate) : undefined,
      createdAt: new Date(milestone.createdAt),
      completedAt: milestone.completedAt ? new Date(milestone.completedAt) : undefined,
      startDate: milestone.startDate ? new Date(milestone.startDate) : undefined
    };
  }

  private parseTaskDates(task: any): DailyTask {
    if (!task) return task;

    return {
      ...task,
      date: new Date(task.date),
      createdAt: new Date(task.createdAt),
      completedAt: task.completedAt ? new Date(task.completedAt) : undefined
    };
  }
}

export const goalsAPI = new GoalsAPI();